import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Send,
  Search,
  MoreVertical,
  Edit3,
  Users,
  MessageCircle,
  Trash2,
  Paperclip,
  X,
  Check,
  CheckCheck,
  Plus,
  ChevronLeft
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { useResponsive } from '../contexts/ResponsiveContext';
import { 
  getUserConversations, 
  getConversation, 
  getConversationMessages,
  sendMessage, 
  markConversationAsRead,
  deleteMessage,
  updateConversationTitle,
  addParticipantToConversation,
  subscribeToConversations,
  subscribeToMessages,
  subscribeToUserMessages,
  searchConversations,
  createConversation,
  createGroupConversation,
  getUserSpecificConversations,
  getUserConversationMessages,
  getUserTotalUnreadCount,
  deleteConversationForUser
} from '../services/messagingService';
import { getInternalUsers } from '../services/userService';
import { getAccounts } from '../services/firestoreService';
import { fetchCommunityDirectory } from '../services/firebase/community';
import { fetchPortalUsersLinkedToAccounts } from '../services/accountTeamMembersService';
import { fetchPublicProfile, fetchPublicProfiles, profileInitials } from '../services/publicProfilesService';
import { isMessagingCommunityClient } from '../utils/crmClientListFilters';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import Modal from '../components/Modal';
import PortalBackground from '../components/PortalBackground';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

// Deterministic gradient per user so initials avatars are distinct and readable.
const AVATAR_GRADIENTS = [
  'from-rose-500 to-red-600',
  'from-amber-500 to-orange-600',
  'from-emerald-500 to-green-600',
  'from-sky-500 to-blue-600',
  'from-violet-500 to-purple-600',
  'from-fuchsia-500 to-pink-600',
  'from-cyan-500 to-teal-600',
  'from-indigo-500 to-blue-700',
];
const gradientForKey = (key = '') => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

// Avatar component for users
const UserAvatar = ({ user = {}, size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-7 h-7 text-[11px]',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
    xl: 'w-14 h-14 text-lg',
  };

  const initials = user.initials || (user.name ? user.name.substring(0, 2).toUpperCase() : '?');
  const gradient = gradientForKey(user.id || user.name || initials);
  const baseClasses = `rounded-full flex items-center justify-center font-semibold select-none shrink-0 ${sizeClasses[size]} ${className}`;

  if (user.profileImage) {
    return (
      <div className={`${baseClasses} overflow-hidden`}>
        <img
          src={user.profileImage}
          alt={user.name || 'User'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }}
        />
        <div className={`w-full h-full items-center justify-center bg-gradient-to-br ${gradient} text-white`} style={{ display: 'none' }}>
          {initials}
        </div>
      </div>
    );
  }

  return (
    <div className={`${baseClasses} bg-gradient-to-br ${gradient} text-white`}>
      {initials}
    </div>
  );
};

// Attachment preview component
const AttachmentPreview = ({ attachment, onRemove }) => {
  const { darkMode } = useTheme();
  
  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎥';
    if (type.startsWith('audio/')) return '🎵';
    if (type.includes('pdf')) return '📄';
    if (type.includes('word') || type.includes('document')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    if (type.includes('powerpoint') || type.includes('presentation')) return '📽️';
    return '📎';
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`flex items-center space-x-3 p-3 rounded-lg border ${
      darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="text-2xl">{getFileIcon(attachment.type)}</div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium truncate ${
          darkMode ? 'text-white' : 'text-gray-900'
        }`}>
          {attachment.name}
        </div>
        <div className={`text-xs ${
          darkMode ? 'text-gray-400' : 'text-gray-500'
        }`}>
          {formatFileSize(attachment.size)}
        </div>
        {attachment.status === 'uploading' && (
          <div className="text-xs text-blue-500">Uploading...</div>
        )}
        {attachment.status === 'error' && (
          <div className="text-xs text-red-500">Upload failed: {attachment.error}</div>
        )}
        {attachment.status === 'uploaded' && (
          <div className="text-xs text-green-500">✓ Uploaded</div>
        )}
      </div>
      <button
        onClick={() => onRemove(attachment.id)}
        className={`p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/20 ${
          darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'
        }`}
      >
        <X size={16} />
      </button>
    </div>
  );
};

const Messages = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const { isOnboardingMode } = useOnboarding();
  const { isMobile, isTablet } = useResponsive();
  
  // State for conversations and messages
  const [conversations, setConversations] = useState([]);
  // Track the selection by id only; the conversation object is derived from the live
  // list below. Storing the object directly meant every subscription tick replaced it
  // with a new reference, which re-ran the message loader and made messages flicker/vanish.
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  
  // State for new message
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredConversations, setFilteredConversations] = useState([]);
  
  // State for modals
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [showConversationSettingsModal, setShowConversationSettingsModal] = useState(false);
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  
  // State for conversation management
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newParticipantId, setNewParticipantId] = useState('');
  
  // State for new conversation creation
  const [newConversationParticipants, setNewConversationParticipants] = useState([]);
  const [showBbaSection, setShowBbaSection] = useState(true);
  const [creatingConversation, setCreatingConversation] = useState(false);
  
  // State for user data
  const [internalUsers, setInternalUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [userCache, setUserCache] = useState({});
  /** accountId -> portal users linked to that account (see fetchPortalUsersLinkedToAccounts) */
  const [linkedPortalUsersByAccountId, setLinkedPortalUsersByAccountId] = useState({});
  
  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const processedMemberRef = useRef(new Set());
  /** Always latest selection for listeners that must not close over stale React state */
  const selectedConversationRef = useRef(null);
  /** Guards async message loads and snapshots so a slower response cannot overwrite another thread */
  const activeMessagesConversationIdRef = useRef(null);

  // Single source of truth for the open thread — always the freshest copy from the list.
  const selectedConversation = useMemo(
    () => conversations.find((c) => c && c.id === selectedConversationId) || null,
    [conversations, selectedConversationId]
  );

  // Stable thread key for loading messages. Stays constant for a given thread even as the
  // conversation document updates, so the message loader runs once per thread, not per tick.
  const activeConversationId = selectedConversation
    ? (selectedConversation.conversationId || selectedConversation.id)
    : null;

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  /** Active paying clients on Lite, Plus, Pro, or Custom — aligned with CRM fulfillment / roadmap lists */
  const messagingCommunityAccounts = useMemo(
    () => accounts.filter((a) => a.inCommunityDirectory || isMessagingCommunityClient(a)),
    [accounts]
  );

  /** Match Community.js + kickoff team linkage: My Profile users per account, plus cache rows by uid and primary by account id */
  useEffect(() => {
    if (!messagingCommunityAccounts.length) {
      setLinkedPortalUsersByAccountId({});
      return;
    }
    let cancelled = false;

    const load = async () => {
      try {
        const map = await fetchPortalUsersLinkedToAccounts(messagingCommunityAccounts);
        if (cancelled) return;

        const obj = {};
        map.forEach((arr, id) => {
          obj[id] = arr;
        });
        setLinkedPortalUsersByAccountId(obj);

        setUserCache((prev) => {
          const next = { ...prev };
          for (const account of messagingCommunityAccounts) {
            const linked = obj[account.id] || [];
            for (const u of linked) {
              next[u.uid] = {
                id: u.uid,
                name: u.name,
                initials:
                  u.firstName && u.lastName
                    ? `${u.firstName[0]}${u.lastName[0]}`.toUpperCase()
                    : u.firstName
                    ? u.firstName.substring(0, 2).toUpperCase()
                    : (u.name || 'M').substring(0, 2).toUpperCase(),
                profileImage: u.profileImage || null,
                firstName: u.firstName || '',
                lastName: u.lastName || '',
                company: account.company || '',
                type: 'portal',
                accountId: account.id,
              };
            }
            const primary = linked.find((u) => u.isPrimary);
            if (primary) {
              next[account.id] = {
                id: primary.uid,
                name: primary.name,
                initials:
                  primary.firstName && primary.lastName
                    ? `${primary.firstName[0]}${primary.lastName[0]}`.toUpperCase()
                    : primary.firstName
                    ? primary.firstName.substring(0, 2).toUpperCase()
                    : (primary.name || 'M').substring(0, 2).toUpperCase(),
                profileImage: primary.profileImage || null,
                firstName: primary.firstName || '',
                lastName: primary.lastName || '',
                company: account.company || '',
                type: 'portal',
                accountId: account.id,
              };
            }
          }
          return next;
        });
      } catch (e) {
        console.warn('Load portal-linked users for messaging community list failed:', e);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [messagingCommunityAccounts]);

  const messagingCommunityRows = useMemo(() => {
    const sorted = [...messagingCommunityAccounts].sort((a, b) =>
      String(a.company || '').localeCompare(String(b.company || ''), undefined, { sensitivity: 'base' })
    );
    const rows = [];
    for (const account of sorted) {
      const linked = linkedPortalUsersByAccountId[account.id];
      const additional = linked ? linked.filter((u) => !u.isPrimary) : [];

      rows.push({
        key: `acc:${account.id}`,
        participantId: account.id,
      });
      for (const u of additional) {
        rows.push({
          key: `mem:${u.uid}`,
          participantId: u.uid,
        });
      }
    }
    return rows;
  }, [messagingCommunityAccounts, linkedPortalUsersByAccountId]);

  /** Community grouped by company so each account's team members are visible under it */
  const messagingCommunityGroups = useMemo(() => {
    const sorted = [...messagingCommunityAccounts].sort((a, b) =>
      String(a.company || '').localeCompare(String(b.company || ''), undefined, { sensitivity: 'base' })
    );
    return sorted.map((account) => {
      const linked = linkedPortalUsersByAccountId[account.id] || [];
      const members = linked.filter((u) => !u.isPrimary);
      return {
        accountId: account.id,
        company: account.company || 'Client',
        // Primary contact is addressed by the account id (resolves to the primary portal user via cache)
        primaryParticipantId: account.id,
        memberParticipantIds: members.map((u) => u.uid),
      };
    });
  }, [messagingCommunityAccounts, linkedPortalUsersByAccountId]);
  
  // Scroll to bottom of messages (e.g. when opening a conversation)
  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }
    });
  };

  // Fetch user data from Firebase
  const fetchUserData = async (userId) => {
    try {
      // Check if user is already in cache
      if (userCache[userId]) {
        return userCache[userId];
      }

      // Participants can be staff, portal users of any company, or a client account id.
      // Clients can only read their own company's users docs (firestore.rules), so the
      // name and photo come from the public profile.
      const profile = await fetchPublicProfile(userId);
      if (profile) {
        const userInfo = {
          id: profile.type === 'portal' && profile.accountId === userId ? profile.id : userId,
          name: profile.name,
          initials: profileInitials(profile),
          // Only use profileImage from My Profile, not from account/contact data
          profileImage: profile.profileImage || null,
          firstName: profile.firstName,
          lastName: profile.lastName,
          company: profile.company || (profile.type === 'internal' ? '' : 'No company'),
          type: profile.type,
          ...(profile.accountId === userId ? { accountId: userId } : {}),
        };
        setUserCache(prev => ({ ...prev, [userId]: userInfo }));
        return userInfo;
      }

      // If nothing found, return fallback
      const fallbackInfo = {
        id: userId,
        name: 'Unknown User',
        initials: 'UU',
        profileImage: null,
        type: 'unknown'
      };
      
      // Cache the fallback
      setUserCache(prev => ({ ...prev, [userId]: fallbackInfo }));
      return fallbackInfo;
    } catch (error) {
      console.error('Error fetching user data:', error);
      const errorInfo = {
        id: userId,
        name: 'Error Loading User',
        initials: 'E',
        profileImage: null,
        type: 'error'
      };
      
      // Cache the error
      setUserCache(prev => ({ ...prev, [userId]: errorInfo }));
      return errorInfo;
    }
  };

  // Get user display info
  const getUserDisplayInfo = (userId) => {
    // Check internal users first
    const internalUser = internalUsers.find(u => u.id === userId);
    if (internalUser) {
      return {
        id: internalUser.id,
        name: internalUser.name,
        initials: internalUser.initials,
        profileImage: internalUser.profileImage,
        type: 'internal'
      };
    }
    
    // Check accounts - but try to get user profile data first (like Community does)
    const account = accounts.find(a => a.id === userId);
    if (account) {
      // Extract account data as base/fallback (like Community processAccountToMember)
      const contact = account.contact || {};
      const accountFirstName = contact.firstName || contact.name?.split(' ')[0] || '';
      const accountLastName = contact.lastName || contact.name?.split(' ').slice(1).join(' ') || '';
      const accountDisplayName = contact.name || (accountFirstName && accountLastName ? `${accountFirstName} ${accountLastName}`.trim() : account.company || 'Client');
      const accountCompany = account.company || 'No company';
      
      // Check if we have user profile data in cache (from fetchUserData or real-time subscription)
      const cachedUser = userCache[userId];
      
      // Use user profile data if available, otherwise fall back to account data (like Community)
      const finalFirstName = cachedUser?.firstName || accountFirstName;
      const finalLastName = cachedUser?.lastName || accountLastName;
      const finalDisplayName = cachedUser?.name || accountDisplayName;
      const finalCompany = cachedUser?.company || accountCompany;
      const finalInitials = cachedUser?.initials || (finalFirstName && finalLastName 
        ? `${finalFirstName[0]}${finalLastName[0]}`.toUpperCase()
        : finalFirstName 
        ? finalFirstName.substring(0, 2).toUpperCase()
        : (finalCompany?.[0] || 'C').toUpperCase());
      
      // If not in cache, trigger async fetch (will update cache when done)
      if (!cachedUser) {
        fetchUserData(userId);
      }
      
      return {
        id: account.id,
        name: finalDisplayName,
        initials: finalInitials,
        // Only My Profile (portal user linked by accountId), same as Community — not CRM contact photo
        profileImage: cachedUser?.profileImage || null,
        type: 'client',
        company: finalCompany,
        firstName: finalFirstName,
        lastName: finalLastName
      };
    }
    
    // Check current user
    if (userId === currentUser?.uid) {
      return {
        id: currentUser.uid,
        name: currentUserProfile?.firstName && currentUserProfile?.lastName
          ? `${currentUserProfile.firstName} ${currentUserProfile.lastName}`
          : currentUserProfile?.firstName
          ? currentUserProfile.firstName
          : currentUser?.displayName || currentUser?.email?.split('@')[0] || 'You',
        initials: currentUserProfile?.firstName && currentUserProfile?.lastName
          ? `${currentUserProfile.firstName[0]}${currentUserProfile.lastName[0]}`.toUpperCase()
          : currentUserProfile?.firstName
          ? currentUserProfile.firstName.substring(0, 2).toUpperCase()
          : (currentUser?.displayName || currentUser?.email?.split('@')[0] || 'U').substring(0, 2).toUpperCase(),
        // Only use profileImage from My Profile, show initials if not available
        profileImage: currentUserProfile?.profileImage || null,
        type: 'current'
      };
    }
    
    // Check cache first
    if (userCache[userId]) {
      return userCache[userId];
    }
    
    // If not in cache, trigger async fetch and return loading state
    fetchUserData(userId);
    return {
      id: userId,
      name: 'Loading...',
      initials: 'L',
      profileImage: null,
      type: 'loading'
    };
  };

  /** Participants other than the signed-in user (empty if uid not in the thread). */
  const getOtherParticipantIds = (conversation) => {
    if (!conversation?.participants || !Array.isArray(conversation.participants)) return [];
    return conversation.participants.filter((id) => id !== currentUser?.uid);
  };

  /** True groups only (3+ people); 1-2 person threads render like DMs. */
  const isGroupConversation = (conversation) => (conversation?.participants?.length || 0) > 2;

  /** The person a 1:1-style thread is "about": the other participant, falling back to the only one stored. */
  const getDmFocusUserId = (conversation) => {
    const others = getOtherParticipantIds(conversation);
    return others[0] || conversation?.participants?.[0] || null;
  };

  /** Old data stored titles like "Chat with Jane" — never show that prefix. */
  const stripChatWithPrefix = (title) => String(title || '').replace(/^chat with\s+/i, '').trim();

  // Get conversation title
  const getConversationTitle = (conversation) => {
    // Add null check
    if (!conversation) {
      return 'Unknown Conversation';
    }

    // Add null check for participants
    if (!conversation.participants || !Array.isArray(conversation.participants)) {
      return 'Invalid Conversation';
    }

    // 1-2 person threads always show the person's name (not the stored title)
    if (!isGroupConversation(conversation)) {
      const focusId = getDmFocusUserId(conversation);
      const name = focusId ? getUserDisplayInfo(focusId).name : '';
      if (name && name !== 'Loading...') return name;
      return stripChatWithPrefix(conversation.title) || name || 'Conversation';
    }

    // For group chats (3+ participants), use custom title if available, otherwise generate one
    const storedTitle = stripChatWithPrefix(conversation.title);
    if (storedTitle && storedTitle !== 'undefined') {
      return storedTitle;
    }

    return `Group Chat (${conversation.participants.length} people)`;
  };

  // Get conversation participants info
  const getConversationParticipantsInfo = (conversation) => {
    // Add null check
    if (!conversation || !conversation.participants || !Array.isArray(conversation.participants)) {
      return [];
    }
    return conversation.participants.map(userId => getUserDisplayInfo(userId));
  };

  // Load conversations
  useEffect(() => {
    if (!currentUser) return;
    
    const loadConversations = async () => {
      try {
        setLoading(true);
        // Use user-specific conversations for better performance
        const conversationsData = await getUserSpecificConversations(currentUser.uid);
        setConversations(conversationsData);
        setFilteredConversations(conversationsData);
        
        // Get total unread count
        const totalUnread = await getUserTotalUnreadCount(currentUser.uid);
        setTotalUnreadCount(totalUnread);
        
        // Check if there's a selected member from Community page
        const selectedMemberData = localStorage.getItem('selectedMemberForMessage');
        if (selectedMemberData) {
          try {
            const selectedMember = JSON.parse(selectedMemberData);

            // Create a unique key for this member + user combination
            const memberKey = `${currentUser.uid}_${selectedMember.id}`;
            
            // Check if we've already processed this member in this session to prevent duplicates
            if (processedMemberRef.current.has(memberKey)) {
              // Clear localStorage but don't process again
              localStorage.removeItem('selectedMemberForMessage');
            } else {
              // Mark as processed immediately to prevent duplicate processing
              processedMemberRef.current.add(memberKey);
              
              // Clear localStorage immediately to prevent duplicate processing on re-renders
              localStorage.removeItem('selectedMemberForMessage');
              
              // Check if conversation already exists with this member
              const existingConversation = conversationsData.find(conv => {
                if (!conv.participants || !Array.isArray(conv.participants)) return false;
                return conv.participants.length === 2 && 
                       conv.participants.includes(currentUser.uid) && 
                       conv.participants.includes(selectedMember.id);
              });
              
              if (existingConversation) {
                // Conversation already exists, select it
                setSelectedConversationId(existingConversation.id);
              } else {
                // Create new conversation with the selected member
                const conversationId = await createConversation({
                  title: selectedMember.name || 'Direct message',
                  participants: [currentUser.uid, selectedMember.id],
                  createdBy: currentUser.uid,
                  isGroup: false
                });
                
                // Reload conversations to include the new one
                const updatedConversations = await getUserSpecificConversations(currentUser.uid);
                setConversations(updatedConversations);
                setFilteredConversations(updatedConversations);
                
                // Find and select the new conversation
                const newConversation = updatedConversations.find(conv => conv.id === conversationId);
                if (newConversation) {
                  setSelectedConversationId(newConversation.id);
                }
              }
            }
          } catch (error) {
            console.error('Error handling selected member:', error);
            // Clear the stored member data on error
            localStorage.removeItem('selectedMemberForMessage');
          }
        }
      } catch (error) {
        console.error('Error loading conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadConversations();
    
    // Subscribe to real-time updates. The selected thread is derived from this list,
    // so we only need to keep the list fresh — no manual re-selection required.
    const unsubscribe = subscribeToConversations(currentUser.uid, (updatedConversations) => {
      setConversations(updatedConversations);
      setFilteredConversations(updatedConversations);
    });

    return unsubscribe;
  }, [currentUser]);

  // Subscribe to user profile updates for all users in conversations
  useEffect(() => {
    if (!currentUser || conversations.length === 0) return;

    // Get all unique user IDs from all conversations
    const allUserIds = new Set();
    conversations.forEach(conv => {
      if (conv.participants && Array.isArray(conv.participants)) {
        conv.participants.forEach(userId => {
          if (userId) allUserIds.add(userId);
        });
      }
    });

    const unsubscribes = [];

    // Names and photos for every participant (other companies' users docs aren't
    // readable from the portal, so these come from public profiles).
    let cancelled = false;
    fetchPublicProfiles([...allUserIds]).then((profiles) => {
      if (cancelled) return;
      setUserCache(prev => {
        const next = { ...prev };
        Object.entries(profiles).forEach(([id, profile]) => {
          next[id] = {
            id: profile.type === 'portal' && profile.accountId === id ? profile.id : id,
            name: profile.name,
            initials: profileInitials(profile),
            // Only use profileImage from My Profile, not from account/contact data
            profileImage: profile.profileImage || null,
            firstName: profile.firstName,
            lastName: profile.lastName,
            company: profile.company || '',
            type: profile.type,
            ...(profile.accountId === id ? { accountId: id } : {}),
          };
        });
        return next;
      });
    });

    allUserIds.forEach(userId => {
      // Staff profiles stay live (internal_users is readable by everyone signed in)
      const internalUserDocRef = doc(db, 'internal_users', userId);
      const unsubscribeInternal = onSnapshot(internalUserDocRef, (internalSnapshot) => {
        if (internalSnapshot.exists()) {
          const userData = internalSnapshot.data();
          const userInfo = {
            id: userId,
            name: userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Internal User',
            initials: userData.firstName && userData.lastName 
              ? `${userData.firstName[0]}${userData.lastName[0]}`.toUpperCase()
              : userData.name
              ? userData.name.substring(0, 2).toUpperCase()
              : 'IU',
            // Only use profileImage from My Profile, not from account/contact data
            profileImage: userData.profileImage || null,
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            type: 'internal'
          };
          
          // Update cache
          setUserCache(prev => ({ ...prev, [userId]: userInfo }));
        }
      }, (error) => {
        // Ignore errors for internal_users if user doesn't exist
      });
      unsubscribes.push(unsubscribeInternal);
    });

    return () => {
      cancelled = true;
      unsubscribes.forEach(unsub => unsub());
    };
  }, [conversations, currentUser]);

  // Function to reload conversations. Selection is derived from the list, so it survives
  // a reload automatically as long as the thread is still present.
  const reloadConversations = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const conversationsData = await getUserSpecificConversations(currentUser.uid);
      setConversations(conversationsData);
      setFilteredConversations(conversationsData);

      const totalUnread = await getUserTotalUnreadCount(currentUser.uid);
      setTotalUnreadCount(totalUnread);
    } catch (error) {
      console.error('Error reloading conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh total unread count
  const refreshTotalUnreadCount = async () => {
    if (!currentUser) return;
    
    try {
      const totalUnread = await getUserTotalUnreadCount(currentUser.uid);
      setTotalUnreadCount(totalUnread);
    } catch (error) {
      console.error('Error refreshing total unread count:', error);
    }
  };

  // Load messages for the open thread. Keyed on the stable thread id (not the conversation
  // object) so it runs once per thread and survives conversation-document updates.
  useEffect(() => {
    if (!activeConversationId || !currentUser?.uid) {
      activeMessagesConversationIdRef.current = null;
      setMessagesLoading(false);
      return;
    }

    const conversationId = activeConversationId;
    activeMessagesConversationIdRef.current = conversationId;
    setMessages([]);
    setMessagesLoading(true);

    let cancelled = false;

    const loadMessages = async () => {
      try {
        const messagesData = await getUserConversationMessages(conversationId);
        if (cancelled || activeMessagesConversationIdRef.current !== conversationId) {
          return;
        }
        setMessages(messagesData);

        try {
          await markConversationAsRead(conversationId, currentUser.uid);
          await refreshTotalUnreadCount();
        } catch (markReadError) {
          console.warn('Error marking conversation as read:', markReadError);
        }
      } catch (error) {
        console.error('Error loading messages:', error);
        // Don't blank the thread on error — an empty list looked like messages were deleted.
      } finally {
        if (!cancelled) {
          setMessagesLoading(false);
        }
      }
    };

    loadMessages();

    const unsubscribe = subscribeToUserMessages(
      conversationId,
      (updatedMessages) => {
        if (cancelled || activeMessagesConversationIdRef.current !== conversationId) {
          return;
        }
        const messagesArray = Array.isArray(updatedMessages) ? updatedMessages : [];
        setMessages(messagesArray);

        if (messagesArray.length > 0) {
          const lastMessage = messagesArray[messagesArray.length - 1];
          if (lastMessage.senderId !== currentUser.uid) {
            markConversationAsRead(conversationId, currentUser.uid);
            refreshTotalUnreadCount();
          }
        }
      },
      () => {
        if (!cancelled) {
          setMessagesLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [activeConversationId, currentUser]);

  // Scroll to bottom when messages change or when conversation is selected and loading completes
  useEffect(() => {
    scrollToBottom();
  }, [messages, messagesLoading]);

  // Filter conversations based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredConversations(conversations);
      return;
    }
    
         const filtered = conversations.filter(conversation => {
       if (!conversation) return false;
       const title = getConversationTitle(conversation).toLowerCase();
       const searchLower = searchTerm.toLowerCase();
       return title.includes(searchLower);
     });
    
    setFilteredConversations(filtered);
  }, [searchTerm, conversations]);

  // Load users and accounts data
  useEffect(() => {
    const loadUsersAndAccounts = async () => {
      try {
        const [users, accountsData] = await Promise.all([
          getInternalUsers(),
          // Staff get every account. A client may only read its own (firestore.rules),
          // so it gets the community directory (already filtered to community clients).
          getAccounts().catch(async () =>
            (await fetchCommunityDirectory()).map((a) => ({ ...a, inCommunityDirectory: true })))
        ]);
        setInternalUsers(users);
        setAccounts(accountsData);
      } catch (error) {
        console.error('Error loading users and accounts:', error);
      }
    };
    
    loadUsersAndAccounts();
  }, []);

  // Load current user profile
  useEffect(() => {
    const loadCurrentUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        // Merge both docs so the profile picture is found wherever it was saved
        const [userDoc, internalUserDoc] = await Promise.all([
          getDoc(doc(db, 'users', currentUser.uid)),
          getDoc(doc(db, 'internal_users', currentUser.uid))
        ]);

        if (!userDoc.exists() && !internalUserDoc.exists()) return;

        const userData = userDoc.exists() ? userDoc.data() : {};
        const internalData = internalUserDoc.exists() ? internalUserDoc.data() : {};

        setCurrentUserProfile({
          ...internalData,
          ...userData,
          firstName: userData.firstName || internalData.firstName || '',
          lastName: userData.lastName || internalData.lastName || '',
          profileImage: userData.profileImage || internalData.profileImage || null
        });
      } catch (error) {
        console.error('Error loading current user profile:', error);
      }
    };
    
    loadCurrentUserProfile();
  }, [currentUser]);

  // Handle user cache updates - re-render when user data is fetched
  useEffect(() => {
    // This effect will trigger re-renders when userCache changes
    // The messages will automatically update with the new user data
  }, [userCache]);

  // Send message
  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;
    if (!selectedConversation || sending) return;

    try {
      setSending(true);

      let uploadedAttachments = [];
      if (attachments.length > 0) {
        uploadedAttachments = await uploadFiles();
      }

      const conversationId = selectedConversation.conversationId || selectedConversation.id;
      await sendMessage(conversationId, currentUser.uid, newMessage.trim(), uploadedAttachments);

      setNewMessage('');
      clearAttachments();
      await refreshTotalUnreadCount();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  // Handle conversation selection
  const handleConversationSelect = (conversation) => {
    if (!conversation || !conversation.id || !conversation.participants || !Array.isArray(conversation.participants)) {
      console.warn('Invalid conversation selected:', conversation);
      return;
    }
    setSelectedConversationId(conversation.id);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle key press in message input
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file attachment
  const handleFileAttachment = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Filter out files that are too large (50MB limit)
      const maxFileSize = 50 * 1024 * 1024; // 50MB
      const validFiles = files.filter(file => file.size <= maxFileSize);
      const invalidFiles = files.filter(file => file.size > maxFileSize);
      
      if (invalidFiles.length > 0) {
        alert(`Some files were too large and were not added:\n${invalidFiles.map(f => f.name).join('\n')}\n\nMaximum file size is 50MB.`);
      }
      
      if (validFiles.length > 0) {
        // Add valid files to attachments state
        const newAttachments = validFiles.map(file => ({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'pending' // pending, uploading, uploaded, error
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
      }
    }
  };

  // Upload files to Firebase Storage
  const uploadFiles = async () => {
    if (attachments.length === 0) return [];
    
    setUploadingFiles(true);
    const uploadedFiles = [];
    
    try {
      for (const attachment of attachments) {
        if (attachment.status === 'pending') {
          // Update status to uploading
          setAttachments(prev => prev.map(a => 
            a.id === attachment.id ? { ...a, status: 'uploading' } : a
          ));
          
          try {
            // Create a unique filename
            const timestamp = Date.now();
            const fileName = `${timestamp}_${attachment.name}`;
            const storageRef = ref(storage, `message-attachments/${selectedConversation.id}/${fileName}`);
            
            // Upload file
            const uploadResult = await uploadBytes(storageRef, attachment.file);
            const downloadURL = await getDownloadURL(uploadResult.ref);
            
            // Update status to uploaded
            setAttachments(prev => prev.map(a => 
              a.id === attachment.id ? { ...a, status: 'uploaded', url: downloadURL } : a
            ));
            
            uploadedFiles.push({
              name: attachment.name,
              size: attachment.size,
              type: attachment.type,
              url: downloadURL
            });
          } catch (error) {
            console.error('Error uploading file:', error);
            // Update status to error
            setAttachments(prev => prev.map(a => 
              a.id === attachment.id ? { ...a, status: 'error', error: error.message } : a
            ));
          }
        } else if (attachment.status === 'uploaded') {
          // File already uploaded, add to result
          uploadedFiles.push({
            name: attachment.name,
            size: attachment.size,
            type: attachment.type,
            url: attachment.url
          });
        }
      }
    } finally {
      setUploadingFiles(false);
    }
    
    return uploadedFiles;
  };

  // Remove attachment
  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  // Clear all attachments
  const clearAttachments = () => {
    setAttachments([]);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Filter out files that are too large (50MB limit)
      const maxFileSize = 50 * 1024 * 1024; // 50MB
      const validFiles = files.filter(file => file.size <= maxFileSize);
      const invalidFiles = files.filter(file => file.size > maxFileSize);
      
      if (invalidFiles.length > 0) {
        alert(`Some files were too large and were not added:\n${invalidFiles.map(f => f.name).join('\n')}\n\nMaximum file size is 50MB.`);
      }
      
      if (validFiles.length > 0) {
        const newAttachments = validFiles.map(file => ({
          id: Date.now() + Math.random(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          status: 'pending'
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
      }
    }
  };

  // Drag and drop visual feedback
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Handle conversation title edit
  const handleTitleEdit = async () => {
    if (!newTitle.trim() || !selectedConversation) return;
    
    try {
      // Use conversationId if available, otherwise use id
      const conversationId = selectedConversation.conversationId || selectedConversation.id;
      await updateConversationTitle(conversationId, newTitle.trim(), currentUser.uid);
      setEditingTitle(false);
      setNewTitle('');
      
      // Refresh conversations to show updated title
      await reloadConversations();
    } catch (error) {
      console.error('Error updating conversation title:', error);
    }
  };

  // Handle adding participant
  const handleAddParticipant = async () => {
    if (!newParticipantId || !selectedConversation) return;
    
    try {
      await addParticipantToConversation(selectedConversation.id, newParticipantId, currentUser.uid);
      setNewParticipantId('');
      setShowAddParticipantModal(false);
    } catch (error) {
      console.error('Error adding participant:', error);
    }
  };

  // Handle deleting conversation for user
  const handleDeleteConversation = () => {
    if (!selectedConversation || !currentUser) return;
    setShowDeleteConfirmationModal(true);
  };

  // Confirm delete conversation
  const confirmDeleteConversation = async () => {
    if (!selectedConversation || !currentUser) return;
    
    try {
      // Use conversationId if available, otherwise use id
      const conversationId = selectedConversation.conversationId || selectedConversation.id;
      await deleteConversationForUser(conversationId, currentUser.uid);
      
      // Close both modals
      setShowDeleteConfirmationModal(false);
      setShowConversationSettingsModal(false);
      
      // Clear selected conversation
      setSelectedConversationId(null);

      // Reload conversations to remove the deleted one
      await reloadConversations();
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Failed to delete conversation: ' + error.message);
    }
  };

  const resetNewConversationModal = () => {
    setShowNewConversationModal(false);
    setNewConversationParticipants([]);
    setShowBbaSection(true);
  };

  /** Stored title for Firestore; UI still uses getConversationTitle for display. */
  const deriveAutoConversationTitle = () => {
    if (newConversationParticipants.length === 1) {
      const participantInfo = getUserDisplayInfo(newConversationParticipants[0]);
      return participantInfo.name || 'Direct message';
    }
    const names = newConversationParticipants
      .map((id) => getUserDisplayInfo(id).name)
      .filter((n) => n && n !== 'Loading...');
    if (names.length === 0) {
      return `Group (${newConversationParticipants.length + 1})`;
    }
    if (names.length <= 3) {
      return names.join(', ');
    }
    return `${names.slice(0, 2).join(', ')} & ${names.length - 2} others`;
  };

  // Handle creating new conversation
  const handleCreateNewConversation = async () => {
    if (newConversationParticipants.length === 0) return;

    try {
      setCreatingConversation(true);

      // Reuse an existing direct thread instead of creating a duplicate
      if (newConversationParticipants.length === 1) {
        const freshConversations = await getUserSpecificConversations(currentUser.uid);
        const existingConversation = freshConversations.find(conv => {
          if (!conv.participants || !Array.isArray(conv.participants)) return false;
          return conv.participants.length === 2 &&
                 conv.participants.includes(currentUser.uid) &&
                 conv.participants.includes(newConversationParticipants[0]);
        });

        if (existingConversation) {
          setConversations(freshConversations);
          setFilteredConversations(freshConversations);
          setSelectedConversationId(existingConversation.id);
          resetNewConversationModal();
          return;
        }
      }

      const allParticipants = [currentUser.uid, ...newConversationParticipants];
      const title = deriveAutoConversationTitle();

      let conversationId;
      if (newConversationParticipants.length === 1) {
        conversationId = await createConversation({
          title,
          participants: allParticipants,
          createdBy: currentUser.uid,
          isGroup: false
        });
      } else {
        conversationId = await createGroupConversation({
          title,
          participants: allParticipants,
          createdBy: currentUser.uid,
          isGroup: true
        });
      }

      resetNewConversationModal();
      await reloadConversations();

      // Select the new thread once it's in the list (the derived memo resolves the object)
      if (conversationId) {
        setSelectedConversationId(conversationId);
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Failed to create conversation: ' + error.message);
    } finally {
      setCreatingConversation(false);
    }
  };

  // Handle adding participant to new conversation
  const handleAddToNewConversation = (participantId) => {
    if (!newConversationParticipants.includes(participantId)) {
      setNewConversationParticipants([...newConversationParticipants, participantId]);
    }
  };

  // Handle removing participant from new conversation
  const handleRemoveFromNewConversation = (participantId) => {
    setNewConversationParticipants(newConversationParticipants.filter(id => id !== participantId));
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  // Format file size for display
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + sizes[i];
  };

  // Normalize Firestore Timestamp | Date | number to millis (0 if unparseable).
  const toMillis = (ts) => {
    if (!ts) return 0;
    if (ts.toMillis) return ts.toMillis();
    if (ts.toDate) return ts.toDate().getTime();
    if (ts instanceof Date) return ts.getTime();
    if (typeof ts === 'number') return ts;
    return 0;
  };

  // Per-user unread count for the badge (unreadCount is a { userId: count } map, not a number).
  const getUnreadCountFor = (conversation) =>
    conversation?.unreadCount?.[currentUser?.uid] || 0;

  // A message I sent is "read" once every other participant's lastReadAt passes its timestamp.
  const isMessageReadByOthers = (message) => {
    if (!selectedConversation) return false;
    const others = getOtherParticipantIds(selectedConversation);
    if (others.length === 0) return false;
    const msgMs = toMillis(message.timestamp);
    if (!msgMs) return false;
    const lastReadAt = selectedConversation.lastReadAt || {};
    return others.every((id) => toMillis(lastReadAt[id]) >= msgMs);
  };

  // Short preview for the sidebar's last-message line.
  const getLastMessagePreview = (conversation) => {
    if (conversation?.lastMessage) return conversation.lastMessage;
    if (conversation?.lastMessageSenderId) return 'Attachment';
    return 'No messages yet';
  };

  if (isOnboardingMode) {
    return (
      <div className={`bg-gray-50 dark:bg-gray-950 min-h-screen ${isMobile ? 'p-3' : isTablet ? 'p-4' : 'p-6'}`}>
        <div className={`${isMobile ? 'w-full' : 'max-w-4xl'} mx-auto`}>
          <div className={`bg-white dark:bg-gray-900 rounded-lg shadow-sm ${isMobile ? 'p-4' : 'p-8'} text-center`}>
            <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Messages
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Complete your onboarding to unlock messaging functionality.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative flex flex-col -m-6 h-[100dvh] overflow-hidden ${isMobile ? '' : 'p-4 lg:p-6'}`}>
      {/* Same ambient BBA background as the Leads, Clients and Tasks pages */}
      <PortalBackground />
      <div className={`relative flex flex-1 min-h-0 w-full flex-row ${
        isMobile
          ? 'bg-white dark:bg-gray-950'
          : 'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden'
      }`}>
        {/* Left Sidebar - Conversations List */}
        <div className={`${
          isMobile
            ? selectedConversationId ? 'hidden' : 'flex w-full'
            : 'flex w-80 lg:w-96 border-r'
        } flex-col shrink-0 bg-gray-50/70 dark:bg-gray-900 border-gray-200 dark:border-gray-800`}>
          {/* Header */}
          <div className="px-4 pt-5 pb-3 shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Messages</h1>
                {totalUnreadCount > 0 && (
                  <div className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-semibold">
                    {totalUnreadCount > 99 ? '99+' : totalUnreadCount}
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowNewConversationModal(true)}
                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-sm transition-colors"
                title="New message"
                aria-label="New message"
              >
                <Plus size={18} />
              </button>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto py-2">
            {loading ? (
              <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                Loading conversations...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <MessageCircle className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm ? 'No conversations found' : 'No conversations yet'}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => setShowNewConversationModal(true)}
                    className="mt-3 text-sm font-medium text-red-500 hover:text-red-600"
                  >
                    Start a conversation
                  </button>
                )}
              </div>
            ) : (
              <div className="px-2 space-y-0.5">
                {filteredConversations
                  .filter(conversation => conversation && conversation.participants && Array.isArray(conversation.participants))
                  .map((conversation) => {
                    const isSelected = selectedConversationId === conversation.id;
                    const isDm = !isGroupConversation(conversation);
                    const unreadCount = getUnreadCountFor(conversation);
                    const hasUnread = unreadCount > 0;
                    const participantInfo = isDm ? getUserDisplayInfo(getDmFocusUserId(conversation)) : null;
                    const lastTime = conversation.lastMessageAt || conversation.lastMessageTime;

                    return (
                      <button
                        key={conversation.id}
                        onClick={() => handleConversationSelect(conversation)}
                        className={`w-full text-left px-2.5 py-2.5 rounded-xl cursor-pointer transition-colors flex items-center gap-3 ${
                          isSelected
                            ? 'bg-red-500/10 dark:bg-red-500/15'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-800/70'
                        }`}
                      >
                        {isDm ? (
                          <UserAvatar user={participantInfo} size="md" />
                        ) : (
                          <div className={`w-10 h-10 shrink-0 rounded-full bg-gradient-to-br ${gradientForKey(conversation.id)} flex items-center justify-center text-white`}>
                            <Users size={18} />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className={`text-sm truncate ${
                              hasUnread
                                ? 'font-semibold text-gray-900 dark:text-white'
                                : 'font-medium text-gray-800 dark:text-gray-200'
                            }`}>
                              {getConversationTitle(conversation)}
                            </h3>
                            {lastTime && (
                              <span className={`text-[11px] shrink-0 ${
                                hasUnread ? 'text-red-500 font-medium' : 'text-gray-400 dark:text-gray-500'
                              }`}>
                                {formatTimestamp(lastTime)}
                              </span>
                            )}
                          </div>

                          <div className="flex items-center justify-between gap-2 mt-0.5">
                            <p className={`text-xs truncate ${
                              hasUnread
                                ? 'text-gray-700 dark:text-gray-200'
                                : 'text-gray-500 dark:text-gray-400'
                            }`}>
                              {isDm && participantInfo?.type === 'client' && participantInfo?.company
                                ? participantInfo.company
                                : getLastMessagePreview(conversation)}
                            </p>
                            {hasUnread && (
                              <span className="shrink-0 min-w-[18px] h-[18px] px-1.5 bg-red-500 text-white text-[11px] font-semibold rounded-full flex items-center justify-center">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
              </div>
            )}
          </div>
        </div>

                 {/* Right Side - Chat Area */}
         <div className={`${isMobile && !selectedConversationId ? 'hidden' : 'flex'} flex-1 flex-col min-w-0 min-h-0 bg-white dark:bg-gray-900`}>
           {selectedConversation && selectedConversation.participants && Array.isArray(selectedConversation.participants) ? (
            <>
                             {/* Chat Header */}
               {selectedConversation && (
                 <div className="px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-800 shrink-0 bg-white dark:bg-gray-900">
                   <div className="flex items-center gap-3 min-w-0">
                     {isMobile && (
                       <button
                         type="button"
                         onClick={() => setSelectedConversationId(null)}
                         className="shrink-0 p-1.5 -ml-1.5 text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                         aria-label="Back to conversations"
                       >
                         <ChevronLeft size={22} />
                       </button>
                     )}
                     {!isGroupConversation(selectedConversation) ? (
                       <UserAvatar
                         user={getUserDisplayInfo(getDmFocusUserId(selectedConversation))}
                         size="lg"
                       />
                     ) : (
                       <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 bg-gradient-to-br ${gradientForKey(selectedConversation.id)} text-white shadow-sm`}>
                         <Users size={22} />
                       </div>
                     )}
                     <div className="flex-1 min-w-0">
                       <h2 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                         {getConversationTitle(selectedConversation)}
                       </h2>
                       {!isGroupConversation(selectedConversation) ? (() => {
                         const participantInfo = getUserDisplayInfo(getDmFocusUserId(selectedConversation));
                         return participantInfo.type === 'client' && participantInfo.company ? (
                           <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                             {participantInfo.company}
                           </p>
                         ) : null;
                       })() : (
                         <p className="text-sm text-gray-500 dark:text-gray-400">
                           {selectedConversation.participants.length} participants
                         </p>
                       )}
                     </div>
                     <button
                       type="button"
                       onClick={() => setShowConversationSettingsModal(true)}
                       className="shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                       aria-label="Conversation settings"
                     >
                       <MoreVertical size={20} />
                     </button>
                   </div>
                 </div>
               )}

                             {/* Messages Area */}
               {selectedConversation && (
                 <div ref={messagesContainerRef} className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6 lg:px-8 py-4 space-y-4 bg-slate-50/60 dark:bg-gray-950/40">
                   {messagesLoading ? (
                     <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                       Loading messages...
                     </div>
                   ) : messages.length === 0 ? (
                     <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                       No messages yet. Say hello!
                     </div>
                   ) : (
                     messages.map((message, index) => {
                       const isOwnMessage = message.senderId === currentUser?.uid;
                       const senderInfo = getUserDisplayInfo(message.senderId);
                       const prev = messages[index - 1];
                       const startsGroup = !prev || prev.senderId !== message.senderId;
                       const isGroupChat = isGroupConversation(selectedConversation);
                       const readByOthers = isOwnMessage && isMessageReadByOthers(message);

                       return (
                         <div
                           key={message.id}
                           className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'} ${startsGroup ? 'mt-3' : 'mt-0.5'}`}
                         >
                           <div className={`flex items-end gap-2 max-w-[min(92%,42rem)] ${isOwnMessage ? 'flex-row-reverse' : ''}`}>
                             <div className="w-7 shrink-0 self-end">
                               {startsGroup && <UserAvatar user={senderInfo} size="sm" />}
                             </div>

                             <div className="min-w-0">
                               {!isOwnMessage && isGroupChat && startsGroup && (
                                 <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-0.5 ml-1">
                                   {senderInfo.name}
                                 </span>
                               )}
                               <div className={`px-3.5 py-2 rounded-2xl ${
                                 isOwnMessage
                                   ? 'bg-red-500 text-white rounded-br-md'
                                   : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border border-gray-200/80 dark:border-gray-700 rounded-bl-md'
                               }`}>
                                  {message.content && (
                                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                                  )}

                                  {/* Display attachments */}
                                  {message.attachments && message.attachments.length > 0 && (
                                    <div className="space-y-1.5 mt-1.5">
                                      {message.attachments.map((attachment, i) => (
                                        <a
                                          key={i}
                                          href={attachment.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className={`flex items-center gap-2 text-sm hover:underline ${
                                            isOwnMessage ? 'text-red-50' : 'text-blue-600 dark:text-blue-400'
                                          }`}
                                        >
                                          <Paperclip size={13} className="shrink-0" />
                                          <span className="truncate max-w-[12rem]">{attachment.name}</span>
                                          <span className="text-xs opacity-75 shrink-0">
                                            ({formatFileSize(attachment.size)})
                                          </span>
                                        </a>
                                      ))}
                                    </div>
                                  )}

                                  <div className={`flex items-center justify-end gap-1 mt-0.5 ${
                                    isOwnMessage ? 'text-red-100/80' : 'text-gray-400 dark:text-gray-500'
                                  }`}>
                                    <span className="text-[10px]">
                                      {formatTimestamp(message.timestamp)}
                                    </span>
                                    {isOwnMessage && (
                                      readByOthers
                                        ? <CheckCheck size={13} className="text-white" title="Read" />
                                        : <Check size={13} title="Sent" />
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                   )}
                   <div ref={messagesEndRef} />
                 </div>
               )}

                             {/* Message Input */}
               {selectedConversation && (
                 <div 
                   className={`px-4 sm:px-6 lg:px-8 py-4 border-t shrink-0 transition-colors ${
                     isDragOver 
                       ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' 
                       : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900'
                   }`}
                   onDragOver={handleDragOver}
                   onDragEnter={handleDragEnter}
                   onDragLeave={handleDragLeave}
                   onDrop={handleDrop}
                 >
                   {/* Drag and drop indicator */}
                   {isDragOver && (
                     <div className="mb-4 p-4 border-2 border-dashed border-blue-400 rounded-xl bg-blue-50 dark:bg-blue-900/20 text-center">
                       <div className="text-blue-600 dark:text-blue-400 font-medium">
                         Drop files here to attach them
                       </div>
                     </div>
                   )}
                   
                   {/* Attachments Preview */}
                   {attachments.length > 0 && (
                     <div className="mb-4 space-y-2">
                       <div className="flex items-center justify-between">
                         <span className={`text-sm font-medium ${
                           darkMode ? 'text-gray-300' : 'text-gray-700'
                         }`}>
                           Attachments ({attachments.length})
                         </span>
                         <button
                           onClick={clearAttachments}
                           className={`text-xs ${
                             darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-500 hover:text-red-600'
                           }`}
                         >
                           Clear all
                         </button>
                       </div>
                       {attachments.map((attachment) => (
                         <AttachmentPreview
                           key={attachment.id}
                           attachment={attachment}
                           onRemove={removeAttachment}
                         />
                       ))}
                     </div>
                   )}
                   
                   <div className="flex items-end space-x-3">
                     <div className="relative">
                       <button
                         onClick={() => fileInputRef.current?.click()}
                         disabled={uploadingFiles}
                         className={`p-2 rounded-lg transition-colors ${
                           uploadingFiles
                             ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                             : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800'
                         }`}
                         title="Attach files"
                       >
                         <Paperclip size={20} />
                       </button>
                       {uploadingFiles && (
                         <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                       )}
                     </div>
                     
                     <div className="flex-1">
                       <textarea
                         value={newMessage}
                         onChange={(e) => setNewMessage(e.target.value)}
                         onKeyPress={handleKeyPress}
                         placeholder="Type a message or drag & drop files here..."
                         rows={1}
                         className="w-full min-h-[44px] max-h-40 px-3.5 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-y"
                       />
                     </div>
                     
                     <button
                       onClick={handleSendMessage}
                       disabled={(!newMessage.trim() && attachments.length === 0) || sending || uploadingFiles}
                       className={`p-2 rounded-lg transition-colors ${
                         (newMessage.trim() || attachments.length > 0) && !sending && !uploadingFiles
                           ? 'bg-red-500 text-white hover:bg-red-600'
                           : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                       }`}
                     >
                       {uploadingFiles ? (
                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                       ) : sending ? (
                         <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                       ) : (
                         <Send size={20} />
                       )}
                     </button>
                   </div>
                   
                   <input
                     ref={fileInputRef}
                     type="file"
                     onChange={handleFileAttachment}
                     className="hidden"
                     accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.mp4,.mp3,.wav,.avi,.mov"
                     multiple
                   />
                   
                 </div>
               )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-slate-50/60 dark:bg-gray-950/40">
              <div className="text-center px-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-sm">
                  <MessageCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1.5">
                  Your messages
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-5">
                  Pick a conversation from the list, or start a new one
                </p>
                <button
                  onClick={() => setShowNewConversationModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-500 text-white text-sm font-medium rounded-xl hover:bg-red-600 shadow-sm transition-colors"
                >
                  <Plus size={16} />
                  New message
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      <Modal isOpen={showNewConversationModal} onClose={resetNewConversationModal}>
        <div className="w-full max-w-2xl">
          <div className="relative flex items-center justify-center mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">New message</h2>
            {newConversationParticipants.length > 0 && (
              <span className="absolute right-0 text-sm text-gray-500 dark:text-gray-400">
                {newConversationParticipants.length} selected
              </span>
            )}
          </div>
          
          {/* Selected Participants */}
          {newConversationParticipants.length > 0 ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To ({newConversationParticipants.length})
              </label>
              <div className="space-y-2">
                {newConversationParticipants.map((participantId) => {
                  const participantInfo = getUserDisplayInfo(participantId);
                  return (
                    <div key={participantId} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <div className="flex items-center space-x-2">
                        <UserAvatar user={participantInfo} size="sm" />
                        <div>
                          <span className="text-sm text-gray-900 dark:text-white">{participantInfo.name}</span>
                          {participantInfo.type === 'client' && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 block">Client Account</span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveFromNewConversation(participantId)}
                        className="p-1 text-red-500 hover:text-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-xl text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Choose people below — group chats use everyone you pick
              </p>
            </div>
          )}

          {/* Section Tabs */}
          <div className="mb-4">
            <div className="flex border-b border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowBbaSection(true)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  showBbaSection
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                BBA ({internalUsers.length})
              </button>
              <button
                onClick={() => setShowBbaSection(false)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  !showBbaSection
                    ? 'border-red-500 text-red-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                Community ({messagingCommunityRows.length})
              </button>
            </div>
          </div>

          {/* BBA Section - Internal Users */}
          {showBbaSection && (
            <div className="mb-4">
              <div className="max-h-64 overflow-y-auto space-y-2">
                {internalUsers.length === 0 ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No internal users available
                  </div>
                ) : (
                  internalUsers.map((user) => {
                    const isSelected = newConversationParticipants.includes(user.id);
                    return (
                      <div
                        key={user.id}
                        onClick={() => isSelected ? handleRemoveFromNewConversation(user.id) : handleAddToNewConversation(user.id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <UserAvatar user={user} size="md" />
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">{user.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400">{user.jobTitle || 'Internal User'}</div>
                            </div>
                          </div>
                          {isSelected && (
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-red-500 font-medium">Selected</span>
                              <Check className="w-5 h-5 text-red-500" />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Community Section - All Users/Accounts */}
          {!showBbaSection && (
            <div className="mb-4">
              <div className="max-h-64 overflow-y-auto space-y-4">
                {messagingCommunityAccounts.length === 0 ? (
                  <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                    No eligible clients right now (same rules as CRM fulfillment lists).
                  </div>
                ) : (
                  messagingCommunityGroups.map((group) => {
                    const participantIds = [group.primaryParticipantId, ...group.memberParticipantIds];
                    return (
                      <div key={group.accountId}>
                        <div className="px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                          {group.company}
                        </div>
                        <div className="space-y-1">
                          {participantIds.map((participantId, idx) => {
                            const isSelected = newConversationParticipants.includes(participantId);
                            const info = getUserDisplayInfo(participantId);
                            const isPrimary = idx === 0;
                            return (
                              <div
                                key={participantId}
                                onClick={() =>
                                  isSelected
                                    ? handleRemoveFromNewConversation(participantId)
                                    : handleAddToNewConversation(participantId)
                                }
                                className={`p-2.5 rounded-lg cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700'
                                    : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-3 min-w-0">
                                    <UserAvatar user={info} size="md" />
                                    <div className="min-w-0">
                                      <div className="font-medium text-gray-900 dark:text-white truncate">
                                        {info.name}
                                      </div>
                                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                        {isPrimary ? 'Primary contact' : 'Team member'}
                                      </div>
                                    </div>
                                  </div>
                                  {isSelected && (
                                    <div className="flex items-center space-x-2 shrink-0">
                                      <span className="text-xs text-red-500 font-medium">Selected</span>
                                      <Check className="w-5 h-5 text-red-500" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            <button
              onClick={resetNewConversationModal}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateNewConversation}
              disabled={newConversationParticipants.length === 0 || creatingConversation}
              className={`px-4 py-2 rounded-lg transition-colors ${
                newConversationParticipants.length > 0
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              {creatingConversation ? 'Starting…' : 'Start'}
            </button>
          </div>
        </div>
      </Modal>

             {/* Conversation Settings Modal */}
       <Modal isOpen={showConversationSettingsModal && !!selectedConversation} onClose={() => setShowConversationSettingsModal(false)} showCloseButton={false}>
         <div className="w-full max-w-md">
           <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4 text-center">Conversation Settings</h2>
           
           {/* Edit Title - Only for group chats (3+ participants) */}
           {selectedConversation && selectedConversation.participants && selectedConversation.participants.length > 2 && (
             <div className="mb-4">
               <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                 Conversation Title
               </label>
               {editingTitle ? (
                 <div className="flex space-x-2">
                   <input
                     type="text"
                     value={newTitle}
                     onChange={(e) => setNewTitle(e.target.value)}
                     className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                     placeholder="Enter new title"
                   />
                   <button
                     onClick={handleTitleEdit}
                     className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                   >
                     <Check size={16} />
                   </button>
                   <button
                     onClick={() => {
                       setEditingTitle(false);
                       setNewTitle('');
                     }}
                     className="px-3 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                   >
                     <X size={16} />
                   </button>
                 </div>
               ) : (
                 <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                   <span className="text-gray-900 dark:text-white">
                     {selectedConversation ? getConversationTitle(selectedConversation) : 'Unknown Conversation'}
                   </span>
                   <button
                     onClick={() => {
                       setEditingTitle(true);
                       setNewTitle(selectedConversation ? getConversationTitle(selectedConversation) : '');
                     }}
                     className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                   >
                     <Edit3 size={16} />
                   </button>
                 </div>
               )}
             </div>
           )}

                     {/* Participants */}
           <div className="mb-4">
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               Participants
             </label>
                              <div className="space-y-2">
                   {selectedConversation && getConversationParticipantsInfo(selectedConversation).length > 0 ? getConversationParticipantsInfo(selectedConversation).map((participant) => (
                    <div key={participant.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <div className="flex items-center space-x-2">
                        <UserAvatar user={participant} size="sm" />
                        <span className="text-sm text-gray-900 dark:text-white">{participant.name}</span>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center text-gray-500 dark:text-gray-400 py-2">
                      No participants found
                    </div>
                  )}
                </div>
          </div>


          <div className="flex justify-between">
            <button
              onClick={handleDeleteConversation}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
            <button
              onClick={() => setShowConversationSettingsModal(false)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>

             {/* Add Participant Modal */}
       <Modal isOpen={showAddParticipantModal && !!selectedConversation} onClose={() => setShowAddParticipantModal(false)}>
         <div className="w-full max-w-md">
           <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Add Participant</h2>
           
           <div className="mb-4">
             <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
               Select User
             </label>
             <select
               value={newParticipantId}
               onChange={(e) => setNewParticipantId(e.target.value)}
               className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
             >
               <option value="">Choose a user...</option>
               {internalUsers && internalUsers.length > 0 ? internalUsers.map((user) => (
                 <option key={user.id} value={user.id}>
                   {user.name} (Internal)
                 </option>
               )) : <option value="" disabled>No internal users available</option>}
               {accounts && accounts.length > 0 ? accounts.map((account) => (
                 <option key={account.id} value={account.id}>
                   {account.company} (Client)
                 </option>
               )) : <option value="" disabled>No accounts available</option>}
             </select>
           </div>

          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setShowAddParticipantModal(false)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddParticipant}
              disabled={!newParticipantId}
              className={`px-4 py-2 rounded-lg transition-colors ${
                newParticipantId
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
              }`}
            >
              Add
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={showDeleteConfirmationModal} onClose={() => setShowDeleteConfirmationModal(false)}>
        <div className="w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              Delete Conversation
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Are you sure you want to delete this conversation? This will remove it from your messages permanently.
            </p>
          </div>
          
          <div className="flex justify-center space-x-3">
            <button
              onClick={() => setShowDeleteConfirmationModal(false)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={confirmDeleteConversation}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors flex items-center space-x-2"
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Messages;
